import {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import {CanvasBridgeRouter} from './bridge-router';
import {packRuntimeSrcdoc} from './runtime-packer';
import {isEnvelope, isTextEditReport} from '../bridge/protocol';
import type {IframeMessage, TextEditReport} from '../bridge/protocol';
import type {CanvasBridgeAdapter} from './adapter';
import type {BridgeObservation} from '../bridge/protocol';

export interface CanvasIframeProps {
  /**
   * Adapter — the data seam. The component subscribes to the adapter on
   * mount and unsubscribes on unmount; the host app controls what the
   * iframe displays by mutating the adapter (e.g.,
   * `adapter.setSource(next)`).
   */
  adapter: CanvasBridgeAdapter;
  /**
   * Optional observer for every bridge message in/out. Used by debug
   * surfaces to render a live log; `undefined` means the router emits
   * nothing.
   */
  onObservation?: (observation: BridgeObservation) => void;
  className?: string;
  style?: CSSProperties;
  title?: string;
  /** Blocks pointer and keyboard interaction with the page, and drops focus held inside it. */
  inert?: boolean;
  /**
   * When true, the iframe's height tracks its content (the runtime
   * reports its scrollHeight via the bridge). The host doc scrolls
   * naturally, like an inline document. When false (default), the
   * iframe fills its parent and scrolls internally.
   */
  autoHeight?: boolean;
  /** Called with the dropped files when the user drops files inside the iframe. */
  onFileDrop?: (files: File[]) => void;
  /** Called with `true` when a file drag enters the iframe, `false` when it leaves or drops. */
  onFileDragStateChange?: (isActive: boolean) => void;
  /**
   * Called when the user presses Cmd/Ctrl+P while focus is inside the iframe.
   * The runtime cancels the native print (which would slice the cross-origin
   * frame) and forwards the intent so the host can run its own print flow.
   */
  onPrintShortcut?: () => void;
  /**
   * Called each time the user commits an inline text edit while text mode
   * is on. An edit whose `after` equals `before` means a previous change on
   * that element was reverted.
   */
  onTextEditChange?: (edit: TextEditReport) => void;
}

// The iframe content is untrusted, so only open link schemes that can't
// execute code in the host or (on Electron, where window.open is routed to
// shell.openExternal) launch arbitrary protocol handlers.
const SAFE_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

function openForwardedLink(href: string): void {
  let protocol: string;
  try {
    protocol = new URL(href).protocol;
  } catch {
    return;
  }
  if (!SAFE_LINK_PROTOCOLS.has(protocol)) {
    return;
  }
  window.open(href, '_blank', 'noopener,noreferrer');
}

export interface CanvasIframeHandle {
  /**
   * Ask the runtime for a self-contained, inert HTML snapshot of its
   * currently-rendered DOM — used by the host to print / export to PDF
   * without granting the live (untrusted) iframe any print capability.
   * Rejects if the iframe isn't mounted or the request times out.
   */
  captureSnapshot(): Promise<{html: string; width: number}>;
  /**
   * Toggle hover-to-edit text mode inside the runtime. No-op before mount;
   * a mode requested before the runtime boots is replayed once it does.
   */
  setTextEditMode(enabled: boolean): void;
  /** Commit the active inline edit and resolve after its change report arrives. */
  commitTextEdits(): Promise<void>;
  /** Snap every pending inline text edit back to its rendered original. */
  resetTextEdits(): void;
}

/**
 * Mounts the canvas runtime iframe and wires it to an adapter.
 *
 * Sandboxed without `allow-same-origin` — the iframe runs in a null origin
 * and cannot reach cookies or the parent DOM. All communication is
 * postMessage. The parent can still call `iframe.contentWindow.postMessage`
 * cross-origin; it just can't read into the iframe's DOM.
 *
 * The router is constructed inside the mount effect because it needs
 * `iframe.contentWindow`, which only exists once the iframe element is in
 * the DOM. We use `useLayoutEffect` (not `useEffect`) so the listener is
 * installed synchronously after the DOM commit, before the browser yields
 * to the iframe's srcDoc parsing — `useEffect` runs after paint and can
 * race with the iframe posting `bootstrap.request` on a fast machine.
 *
 * The router is intentionally kept inside this component — the host app
 * interacts with the runtime only through the adapter, so there's no way
 * to capture a stale router reference and use it after unmount.
 */
export const CanvasIframe = forwardRef<CanvasIframeHandle, CanvasIframeProps>(
  function CanvasIframe(
    {
      adapter,
      onObservation,
      className,
      style,
      title = 'Canvas',
      inert = false,
      autoHeight = false,
      onFileDrop,
      onFileDragStateChange,
      onPrintShortcut,
      onTextEditChange,
    },
    ref,
  ) {
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const routerRef = useRef<CanvasBridgeRouter | null>(null);
    const [contentHeight, setContentHeight] = useState<number | null>(null);

    useImperativeHandle(
      ref,
      () => ({
        captureSnapshot: () => {
          const router = routerRef.current;
          if (!router) {
            return Promise.reject(new Error('canvas iframe not ready'));
          }
          return router.requestSnapshot();
        },
        setTextEditMode: (enabled) => {
          routerRef.current?.setTextEditMode(enabled);
        },
        commitTextEdits: () => {
          const router = routerRef.current;
          if (!router) {
            return Promise.reject(new Error('canvas iframe not ready'));
          }
          return router.commitTextEdits();
        },
        resetTextEdits: () => {
          routerRef.current?.resetTextEdits();
        },
      }),
      [],
    );
    // Latest-callback refs so consumers can swap callbacks without forcing the
    // iframe to remount.
    const onObservationRef = useRef(onObservation);
    onObservationRef.current = onObservation;
    const onFileDropRef = useRef(onFileDrop);
    onFileDropRef.current = onFileDrop;
    const onFileDragStateChangeRef = useRef(onFileDragStateChange);
    onFileDragStateChangeRef.current = onFileDragStateChange;
    const onPrintShortcutRef = useRef(onPrintShortcut);
    onPrintShortcutRef.current = onPrintShortcut;
    const onTextEditChangeRef = useRef(onTextEditChange);
    onTextEditChangeRef.current = onTextEditChange;

    useLayoutEffect(() => {
      const iframe = iframeRef.current;
      if (!iframe) {
        return;
      }
      const iframeWindow = iframe.contentWindow;
      if (!iframeWindow) {
        return;
      }

      setContentHeight(null);

      const router = new CanvasBridgeRouter(
        iframeWindow,
        adapter,
        autoHeight ? setContentHeight : undefined,
      );
      routerRef.current = router;
      const unobserve = router.observe((obs) => {
        onObservationRef.current?.(obs);
      });

      // Host-UI messages (drag state, print intent) are handled here rather
      // than inside CanvasBridgeRouter so the router stays focused on the
      // adapter data protocol and doesn't grow adapter interface members for
      // what are really parent-UI concerns.
      const handleIframeUiMessage = (event: MessageEvent): void => {
        if (event.source !== iframeWindow) {
          return;
        }
        if (!isEnvelope(event.data)) {
          return;
        }
        const msg = event.data.message as IframeMessage;
        if (msg.kind === 'drag.over') {
          onFileDragStateChangeRef.current?.(true);
        } else if (msg.kind === 'drag.end') {
          onFileDragStateChangeRef.current?.(false);
        } else if (msg.kind === 'drag.drop') {
          onFileDragStateChangeRef.current?.(false);
          onFileDropRef.current?.(msg.files);
        } else if (msg.kind === 'print.request') {
          onPrintShortcutRef.current?.();
        } else if (msg.kind === 'link.open') {
          openForwardedLink(msg.href);
        } else if (msg.kind === 'textedit.change') {
          if (isTextEditReport(msg.edit)) {
            onTextEditChangeRef.current?.(msg.edit);
          }
        }
      };
      window.addEventListener('message', handleIframeUiMessage);

      return () => {
        unobserve();
        router.dispose();
        routerRef.current = null;
        window.removeEventListener('message', handleIframeUiMessage);
      };
    }, [adapter, autoHeight]);

    const resolvedHeight =
      autoHeight && contentHeight != null ? `${contentHeight}px` : '100%';

    return (
      <iframe
        ref={iframeRef}
        srcDoc={packRuntimeSrcdoc()}
        title={title}
        inert={inert}
        className={className}
        style={{
          border: 'none',
          width: '100%',
          height: resolvedHeight,
          ...style,
        }}
        /*
        Sandbox flags:
          allow-scripts  — Sucrase, React, and user-generated TSX need JS.

        Deliberately omitted:
          allow-modals       — no window.print / alert / confirm / prompt.
                               PDF export goes through Electron printToPDF
                               on a snapshot of the iframe's DOM, not via
                               window.print() from inside.
          allow-forms        — v0 entities don't emit forms; add back if
                               DocumentRequest or Form ships. Paired with
                               CSP `form-action 'none'` for defense in depth.
          allow-same-origin  — load-bearing; keeps the iframe null-origin
                               so it can't reach cookies, localStorage, or
                               the parent DOM.
          allow-popups       — no window.open. Link clicks are forwarded to
                               the host (LINK_CLICK_FORWARDING_SCRIPT), which
                               opens vetted URLs itself.
          allow-top-navigation* — no navigating the top frame away.
          allow-downloads*   — downloads are triggered from the PARENT via
                               the bridge adapter, not from the iframe.
      */
        sandbox="allow-scripts"
      />
    );
  },
);
